import { Component, OnInit, OnDestroy, inject, input, output, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormGroup, FormBuilder, Validators } from '@angular/forms';
import { InputTextModule } from 'primeng/inputtext';
import { TextareaModule } from 'primeng/textarea';
import { CalendarModule } from 'primeng/calendar';
import { DropdownModule } from 'primeng/dropdown';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { Subject, takeUntil } from 'rxjs';
import { SopResponseDto } from '@qc/shared';
import { parseDateOnly } from '@/app/shared/utils';

export interface SopFormData {
    id?: string;
    zenqmsId?: string;
    sopNumber: string;
    sopTitle?: string;
    sopRevision?: string;
    revisionDate?: Date;
    nextReviewDate?: Date;
    documentUrl?: string;
    managedBy?: string;
    context?: string;
    qncSop?: boolean;
    lastSyncedAt?: Date;
    syncStatus?: string;
    warehouseDetailId?: string;
}

@Component({
    selector: 'qc-sop-form',
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        InputTextModule,
        TextareaModule,
        CalendarModule,
        DropdownModule,
        ButtonModule,
        CheckboxModule,
    ],
    template: `
        <form [formGroup]="form" class="space-y-6">
            <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div class="form-field">
                    <label class="block text-sm font-medium text-gray-700 mb-1">SOP Number *</label>
                    <input
                        pInputText
                        formControlName="sopNumber"
                        placeholder="e.g., ACME-QA-SOP-042"
                        class="w-full"
                        [class.border-rose-500]="hasFieldError('sopNumber')"
                        [readonly]="readonly()"
                    />
                    <div *ngIf="getFieldError('sopNumber')" class="text-rose-500 text-sm mt-1">
                        {{ getFieldError('sopNumber') }}
                    </div>
                </div>

                <div class="form-field">
                    <label class="block text-sm font-medium text-gray-700 mb-1">ZenQMS ID</label>
                    <input
                        pInputText
                        formControlName="zenqmsId"
                        placeholder="ZEN-5432"
                        class="w-full"
                        [readonly]="readonly()"
                    />
                </div>

                <div class="form-field">
                    <label class="block text-sm font-medium text-gray-700 mb-1">SOP Title</label>
                    <input
                        pInputText
                        formControlName="sopTitle"
                        placeholder="Product Complaint"
                        class="w-full"
                        [readonly]="readonly()"
                    />
                </div>

                <div class="form-field">
                    <label class="block text-sm font-medium text-gray-700 mb-1">SOP Revision</label>
                    <input
                        pInputText
                        formControlName="sopRevision"
                        placeholder="Managed by MAH / 4.0"
                        class="w-full"
                        [readonly]="readonly()"
                    />
                </div>

                <div class="form-field">
                    <label class="block text-sm font-medium text-gray-700 mb-1">Revision Date</label>
                    <p-calendar
                        formControlName="revisionDate"
                        dateFormat="yy-mm-dd"
                        [showIcon]="true"
                        styleClass="w-full"
                        [readonlyInput]="readonly()"
                    />
                </div>

                <div class="form-field">
                    <label class="block text-sm font-medium text-gray-700 mb-1">Next Review Date</label>
                    <p-calendar
                        formControlName="nextReviewDate"
                        dateFormat="yy-mm-dd"
                        [showIcon]="true"
                        styleClass="w-full"
                        [readonlyInput]="readonly()"
                    />
                </div>

                <div class="form-field">
                    <label class="block text-sm font-medium text-gray-700 mb-1">Document URL</label>
                    <input
                        pInputText
                        formControlName="documentUrl"
                        placeholder="https://qnc.zenqms.com/docs/ACME-QA-SOP-042"
                        class="w-full"
                        [readonly]="readonly()"
                    />
                </div>

                <div class="form-field">
                    <label class="block text-sm font-medium text-gray-700 mb-1">Managed By</label>
                    <input
                        pInputText
                        formControlName="managedBy"
                        placeholder="MAH / 3PL QA"
                        class="w-full"
                        [readonly]="readonly()"
                    />
                </div>

                <div class="form-field">
                    <label class="block text-sm font-medium text-gray-700 mb-1">Context</label>
                    <input
                        pInputText
                        formControlName="context"
                        placeholder="ClientComplaint, PV, 3PL_Receiving"
                        class="w-full"
                        [readonly]="readonly()"
                    />
                </div>

                <div class="form-field">
                    <label class="block text-sm font-medium text-gray-700 mb-1">Q&C SOP</label>
                    <div class="flex items-center gap-3 mt-2">
                        <p-checkbox formControlName="qncSop" [binary]="true" inputId="qncSop" [disabled]="readonly()" />
                        <label for="qncSop" class="cursor-pointer">Yes</label>
                    </div>
                </div>

                <div class="form-field">
                    <label class="block text-sm font-medium text-gray-700 mb-1">Sync Status</label>
                    <p-dropdown
                        formControlName="syncStatus"
                        [options]="syncStatusOptions"
                        optionLabel="label"
                        optionValue="value"
                        placeholder="Select status"
                        styleClass="w-full"
                        [disabled]="readonly()"
                    />
                </div>
            </div>

            <div *ngIf="!readonly() && showButtons()" class="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                <p-button
                    type="button"
                    severity="secondary"
                    variant="outlined"
                    (click)="cancel.emit()"
                    label="Cancel"
                    size="small"
                />
                <p-button
                    type="button"
                    severity="primary"
                    (click)="handleSave()"
                    [label]="isEditMode() ? 'Save Changes' : 'Create SOP'"
                    size="small"
                    [disabled]="!form.valid || saving()"
                />
            </div>
        </form>
    `,
    styles: [
        `
            :host ::ng-deep .p-calendar {
                z-index: 9999;
            }
            :host ::ng-deep .p-dropdown-panel {
                z-index: 9999;
            }
            :host ::ng-deep .p-calendar-panel {
                position: absolute !important;
                z-index: 10000 !important;
                top: auto !important;
                left: auto !important;
            }
        `,
    ],
})
export class SopFormComponent implements OnInit, OnDestroy {
    private fb = inject(FormBuilder);
    private destroy$ = new Subject<void>();

    sop = input<SopResponseDto | null>(null);
    readonly = input<boolean>(false);
    showButtons = input<boolean>(true);

    save = output<SopFormData>();
    cancel = output<void>();
    validityChange = output<boolean>();
    loading = output<boolean>();

    form!: FormGroup;
    saving = signal<boolean>(false);

    syncStatusOptions = [
        { label: 'Success', value: 'Success' },
        { label: 'Pending', value: 'Pending' },
        { label: 'Error', value: 'Error' },
    ];

    ngOnInit(): void {
        this.initializeForm();
        this.setupFormWatchers();
        this.populateFormData();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    private initializeForm(): void {
        this.form = this.fb.group({
            id: [''],
            zenqmsId: [''],
            sopNumber: ['', [Validators.required]],
            sopTitle: [''],
            sopRevision: [''],
            revisionDate: [''],
            nextReviewDate: [''],
            documentUrl: [''],
            managedBy: [''],
            context: [''],
            qncSop: [false],
            lastSyncedAt: [''],
            syncStatus: [''],
            warehouseDetailId: [''],
        });
    }

    private setupFormWatchers(): void {
        this.form.statusChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
            this.validityChange.emit(this.form.valid);
        });
    }

    private populateFormData(): void {
        const sop = this.sop();
        if (sop) {
            const formData = {
                ...sop,
                revisionDate: parseDateOnly(sop.revisionDate),
                nextReviewDate: parseDateOnly(sop.nextReviewDate),
                lastSyncedAt: sop.lastSyncedAt ? new Date(sop.lastSyncedAt) : null,
            };
            this.form.patchValue(formData);
        }
    }

    hasFieldError(fieldName: string): boolean {
        const field = this.form.get(fieldName);
        return !!(field && field.invalid && (field.dirty || field.touched));
    }

    getFieldError(fieldName: string): string | null {
        const field = this.form.get(fieldName);
        if (!field || !field.errors || !field.touched) {
            return null;
        }

        if (field.errors['required']) {
            return `${fieldName} is required`;
        }

        return 'Invalid field';
    }

    isEditMode(): boolean {
        return !!this.sop()?.id;
    }

    handleSave(): void {
        if (!this.form.valid || this.saving()) {
            return;
        }

        this.saving.set(true);
        this.loading.emit(true);
        const formData: SopFormData = this.form.value;

        this.save.emit(formData);
    }
}
